import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { darken, makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import BarChartIcon from '@material-ui/icons/BarChart';
import EmojiEventsOutlinedIcon from '@material-ui/icons/EmojiEventsOutlined';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { Link as RouterLink } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ButtonTo from '../../../components/buttonTo';
import EnhancedTable from '../../../components/table';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { BanReason, Driver } from '../../../redux/reducers/api-types';
import { FormatType, HeadCell } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
// Ressources
import { paths } from '../../paths';

const useStyles = makeStyles((theme) => ({
    statsIcon: {
        opacity: 0.12,
        '&:hover': {
            opacity: 0.3,
        },
    },
    // Same spacing as the issues button, which it sits next to
    topButton: {
        alignSelf: 'flex-end',
        padding: '6px 16px',
        marginRight: 12,
        textTransform: 'none',
    },
    // Red to flag the drivers needing an action; replaces ButtonTo's "rectangle" class, hence its spacing
    issuesButton: {
        alignSelf: 'flex-end',
        padding: '6px 16px',
        textTransform: 'none',
        color: theme.palette.common.white,
        backgroundColor: theme.palette.colors.red,
        '&:hover': {
            backgroundColor: darken(theme.palette.colors.red, 0.15),
        },
    },
}));

interface DriversListPageProps extends WithTranslation {}

const DriversListPage = ({ t }: DriversListPageProps): JSX.Element => {
    const classes = useStyles();

    const headerBuilder = (): HeadCell[] => {
        const headers: HeadCell[] = [
            createCell(t, Cell.ID, { sortable: true }),
            { id: 'fullname', label: t('common:form.firstname') },
            createCell(t, Cell.EMAIL),
            createCell(t, Cell.PHONE_NUMBER),
            { id: 'partner.name', label: t('common:form.partner') },
            { id: 'details.controlTower.name', label: t('control_tower') },
            {
                // Creation date of the account, stored on the user as its subscription date
                id: 'subscription_date',
                label: t('creation_date'),
                type: FormatType.DateShort,
                filterable: false,
            },
            { id: 'banned', label: t('common:suspended'), type: FormatType.Boolean },
            {
                // A driver waiting for a validation is banned with the "new_user" reason
                id: 'ban_reason',
                label: t('to_validate'),
                type: FormatType.Boolean,
                sortable: false,
                // Only "yes" is offered : the API filters on equality, so "not a new driver"
                // (ban_reason null or conformity) cannot be expressed as a single filter
                pickerOptions: [
                    { value: '', label: '' },
                    { value: BanReason.BANNED_NEW_USER, label: t('common:true') },
                ],
                formatCell: (row: Driver) =>
                    row.ban_reason === BanReason.BANNED_NEW_USER ? t('common:true') : t('common:false'),
            },
        ];
        return headers;
    };

    return (
        <App
            disableBreadcrumb
            header={{
                title: t('title'),
                subtitle: t('description'),
                button: (
                    <>
                        <IconButton
                            size="small"
                            className={classes.statsIcon}
                            component={RouterLink}
                            to={paths.driversStats}
                        >
                            <BarChartIcon fontSize="small" />
                        </IconButton>
                        <ButtonTo
                            to={paths.driversTop}
                            startIcon={<EmojiEventsOutlinedIcon />}
                            text={t('top.button')}
                            className={classes.topButton}
                        />
                        <ButtonTo
                            to={paths.driversIssues}
                            startIcon={<ReportProblemOutlinedIcon />}
                            text={t('issues.button')}
                            className={classes.issuesButton}
                        />
                    </>
                ),
            }}
        >
            <EnhancedTable headers={headerBuilder()} type={tableRequestPoints.DRIVERS} />
        </App>
    );
};

export default withTranslation(['driver', 'common'])(DriversListPage);
